import { getLineItemPaidPriceInCents } from "./paid-price.lib";
import type {
  CartItem,
  DraftLineItem,
  FinancialSummary,
  Order,
  OrderLineItem,
  Resolution,
} from "../types";

export function getOrderCurrency(order: Order | null | undefined): string {
  return order?.currency?.customerCurrency ?? "EUR";
}

/**
 * Returns 1 + effective tax rate for HT shops (taxesIncluded=false), or 1 for TTC shops.
 *
 * Used to convert rebuy cart items (always HT on Shopify storefronts for HT shops) to TTC,
 * so they can be compared on the same basis as the item totals, which are all TTC.
 *
 * `unitTaxes[].amount` is per unit (see `getPaidPriceInCents`), so no division by the
 * quantity here: doing it divided the derived rate by the quantity bought (BBS-309).
 *
 * Two strategies to derive the rate, in order:
 * 1. Sum unitTaxes[].rate (EU VAT shops that store an explicit rate).
 * 2. Derive rate from totalTax / unitPrice (US/CA Shopify shops that store rate=null but
 *    always provide amounts — e.g. Texas State Tax + Dallas City Tax bundled on one line).
 *    Mathematically equivalent to the stored rate; avoids the null-rate fallback that
 *    left taxMultiplier=1 for the exact scenario described in BBS-190 (CA 850$ HT → 960.5$
 *    TTC, phantom 110.5$ refund).
 *
 * Guard: only applies the multiplier when the unitTaxes amounts are non-zero.
 * Old DB records may have rate>0 but amount=0 — in that case getPaidPriceInCents also added
 * 0, so keeping multiplier=1 preserves HT/HT consistency and avoids making things worse.
 */
export function getEffectiveTaxMultiplier(
  lineItems: DraftLineItem[],
  order: Order,
): number {
  for (const item of lineItems) {
    if (item.resolution !== "exchange-credit") continue;
    const li = order.lineItems?.find(
      (oli: OrderLineItem) => oli.line_item_id === item.lineItemId,
    );
    if (!li) continue;
    const additionalTaxes = li.unitTaxes.filter((t) => !t.includedInPrice);
    if (additionalTaxes.length === 0) return 1; // TTC shop — no conversion needed

    // Guard: only proceed when actual tax amounts are stored (amount > 0).
    // If amount=0 for all (old records), the paid price also added 0 — keep HT/HT.
    const totalAdditionalTax = additionalTaxes.reduce(
      (sum, t) => sum + t.amount,
      0,
    );
    if (totalAdditionalTax === 0) return 1;

    // Strategy 1: use stored rates (EU VAT shops, rate is explicit).
    const additionalRate = additionalTaxes
      .filter((t) => (t.rate ?? 0) > 0)
      .reduce((sum, t) => sum + (t.rate ?? 0), 0);
    if (additionalRate > 0) return 1 + additionalRate;

    // Strategy 2: derive effective rate from amount / unit price.
    // Handles US/CA Shopify shops that split taxes into named components with rate=null
    // (e.g. "California State Tax", "Los Angeles County Tax", "Dallas City Tax").
    // Both terms are per unit: `price` is `originalUnitPriceSet` and `unitTaxes[].amount`
    // was already divided by the line quantity in `ShopifyOrderNormalizeDto`.
    const price = parseFloat(li.price);
    const effectiveRate = price > 0 ? totalAdditionalTax / price : 0;
    if (effectiveRate > 0) return 1 + effectiveRate;
  }
  return 1;
}

type ItemTotals = Record<
  "refundTotal" | "discountTotal" | "exchangeVariantTotal" | "exchangeCreditTotal",
  number
>;

const TOTAL_BY_RESOLUTION: Record<Resolution, keyof ItemTotals> = {
  refund: "refundTotal",
  discount: "discountTotal",
  exchange: "exchangeVariantTotal",
  "exchange-credit": "exchangeCreditTotal",
};

export function calculateTotals(
  lineItems: DraftLineItem[],
  order: Order,
  rebuyItems: CartItem[] = [],
): FinancialSummary {
  // lineItem.price is already the customer's presentment amount (backend maps it from
  // originalUnitPriceSet.presentmentMoney), so totals below need no extra conversion —
  // only the currency label was wrong (BBS-236).
  const currency = getOrderCurrency(order);

  // All item totals are TTC and net of allocated discounts, like the legacy portal's
  // receipt (`apps/return-widget/src/hooks/receipt/draft-return-request/*.ts`), so the
  // two portals announce the same amount for the same basket (BBS-309).
  const totals: ItemTotals = {
    refundTotal: 0,
    discountTotal: 0,
    exchangeVariantTotal: 0,
    exchangeCreditTotal: 0,
  };

  for (const item of lineItems) {
    if (!item.resolution) continue;
    const unitPrice = getLineItemPaidPriceInCents(order, item.lineItemId);
    totals[TOTAL_BY_RESOLUTION[item.resolution]] += unitPrice * item.quantity;
  }

  // For HT shops, Shopify's /cart.js returns prices WITHOUT tax (HT).
  // Apply the same tax multiplier derived from the exchange-credit items so that
  // rebuyTotal is on a TTC basis and can be compared with the other totals.
  // For TTC shops the multiplier is 1 (no-op) and Shopify cart already returns TTC.
  const taxMultiplier = getEffectiveTaxMultiplier(lineItems, order);
  const rebuyTotal = rebuyItems.reduce(
    (sum, item) => sum + item.price * item.quantity * taxMultiplier,
    0,
  );
  const exchangeTotal = totals.exchangeVariantTotal + totals.exchangeCreditTotal;

  // Exchange items are cost-neutral: the customer returns their item (credit = original
  // price) and receives a new variant (cost = same price, since we use the original
  // price as a proxy). The two cancel out, so exchangeVariantTotal is excluded from
  // amountDue. Actual price-difference charges are handled by the backend via
  // createPaymentIntent.
  // BBS-237: round before comparing against the cents-based payment threshold
  // (see STRIPE_COMPLEMENT_MINIMUM_AMOUNT_IN_CENTS on the backend) — the tax
  // multiplier above makes this a float, and float drift can otherwise push a
  // value that should sit exactly on the threshold to the wrong side of it.
  const amountDue = Math.round(
    Math.max(
      0,
      rebuyTotal -
        totals.exchangeCreditTotal -
        totals.refundTotal -
        totals.discountTotal,
    ),
  );

  return {
    refundTotal: totals.refundTotal,
    exchangeTotal,
    exchangeCreditTotal: totals.exchangeCreditTotal,
    discountTotal: totals.discountTotal,
    rebuyTotal,
    amountDue,
    currency,
    taxMultiplier,
  };
}
